// SPDX-License-Identifier: AGPL-3.0-or-later

import type {CosmeticItem, CosmeticSelection, CosmeticType} from '@app/features/cosmetics/state/Cosmetics';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import {shopSetArtKey} from '@app/features/shop/utils/ShopSets';

export type InventoryFilter = 'all' | 'owned' | 'locked';

export interface InventoryQuery {
	type: CosmeticType | 'all';
	filter: InventoryFilter;
	search: string;
	set: string | null;
}

// The frame and the ultra decoration share the avatar, so the server keeps one of them; the picker does the same.
const AVATAR_PAIR: Partial<Record<CosmeticType, CosmeticType>> = {
	avatar_decoration: 'avatar_ultra',
	avatar_ultra: 'avatar_decoration',
};

const collator = new Intl.Collator('tr');

export function isCosmeticLocked(item: CosmeticItem, isPremium: boolean): boolean {
	return !item.owned && item.availability === 'premium' && !isPremium;
}

function searchable(text: string): string {
	return text
		.toLocaleLowerCase('tr')
		.replaceAll('ı', 'i')
		.normalize('NFD')
		.replace(/\p{Mn}/gu, '');
}

export function filterInventory(
	items: ReadonlyArray<CosmeticItem>,
	query: InventoryQuery,
	isPremium: boolean,
	locale?: string,
): Array<CosmeticItem> {
	const search = searchable(query.search.trim());
	return items
		.filter((item) => {
			const locked = isCosmeticLocked(item, isPremium);
			if (query.type !== 'all' && item.type !== query.type) return false;
			if (query.filter === 'owned' && !item.owned) return false;
			if (query.filter === 'locked' && !locked) return false;
			if (query.set !== null && shopSetArtKey(item.name) !== query.set) return false;
			return search === '' || searchable(`${item.name} ${item.name_en ?? ''}`).includes(search);
		})
		.sort(
			(a, b) =>
				Number(isCosmeticLocked(a, isPremium)) - Number(isCosmeticLocked(b, isPremium)) ||
				collator.compare(cosmeticName(a, locale), cosmeticName(b, locale)),
		);
}

export function inventorySets(items: ReadonlyArray<CosmeticItem>): Array<string> {
	const sets = new Set<string>();
	for (const item of items) {
		const key = shopSetArtKey(item.name);
		if (key) sets.add(key);
	}
	return [...sets];
}

export function pickCosmetic(
	draft: CosmeticSelection,
	type: CosmeticType,
	itemId: string | null,
	wornId: (type: CosmeticType) => string | null,
): {draft: CosmeticSelection; takenOff: CosmeticType | null} {
	const next = {...draft, [type]: itemId};
	const other = AVATAR_PAIR[type];
	if (itemId === null || !other) return {draft: next, takenOff: null};
	const otherId = other in draft ? (draft[other] ?? null) : wornId(other);
	if (otherId === null) return {draft: next, takenOff: null};
	return {draft: {...next, [other]: null}, takenOff: other};
}
